import { notification } from 'antd';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import { JobPostingDetail } from '../types';
import { EmailCommunicationSerializer } from '../types/api';
import { buildCandidateQueryParam, buildRecruitmentProcessQueryParam } from '../utils';
import { EmailFormValues } from './EmailForm';
import SendTestsModal, { SendTestsModalProps } from './SendTestsModal';
import { TestsFormValues } from './TestsForm';

interface BulkSendTestsModalProps extends Omit<SendTestsModalProps, 'helmetTitle'> {
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (
    data: void,
    variables: TestsFormValues & EmailFormValues,
    context: unknown
  ) => void | Promise<unknown>;
  functionId?: JobPostingDetail['function_id'];
  totalCount?: number;
}

const BulkSendTestsModal = ({
  jobPostingId,
  onSuccess,
  functionId,
  totalCount,
  ...props
}: BulkSendTestsModalProps) => {
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const mutation = useAPIMutation(
    async (
      {
        testTypeList,
        message,
        attachments,
        fromName,
        subject,
        enableReplyTo,
        replyTo,
      }: TestsFormValues & EmailFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      if (jobPostingId) {
        await api.post(
          '/recruitment-processes-actions/send-email-to-all/',
          {
            from_name: fromName,
            job_posting: jobPostingId,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'TESTS',
            tests: testTypeList as EmailCommunicationSerializer['tests'],
            attachments: attachmentIds,
          },
          {
            params: await buildRecruitmentProcessQueryParam(
              searchParams,
              api,
              jobPostingId,
              functionId
            ),
          }
        );
      } else {
        await api.post(
          '/candidates-actions/send-email-to-all/',
          {
            from_name: fromName,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'TESTS',
            tests: testTypeList as EmailCommunicationSerializer['tests'],
            attachments: attachmentIds,
          },
          {
            params: buildCandidateQueryParam(searchParams),
          }
        );
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/email-communications/');
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.error === 'empty_queryset_send_email_to_all') {
          return notification.error({
            duration: 0,
            message: 'Erro',
            description:
              'Não é possível realizar esta ação, pois não há candidatos a serem selecionados. Recarregue a página e tente novamente.',
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  return (
    <SendTestsModal
      title={
        <span>
          Enviar testes para <strong>{totalCount ?? '...'} candidato(s)</strong>
        </span>
      }
      helmetTitle={`Enviar testes para ${totalCount ?? '...'} candidato(s)`}
      jobPostingId={jobPostingId}
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      loading={mutation.isLoading}
      {...props}
    />
  );
};

export default BulkSendTestsModal;
